import { CloseCircleFilled, LeftOutlined, QuestionCircleOutlined, RightOutlined } from "@ant-design/icons";
import { Card, Col, DatePicker, message, Modal, Row, Select, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { technicalCommissionApi } from "api/technicalCommission.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { getTableShowingRange, StatusCell, TableContent, TableLayout, TableLayoutFooter } from "components/table";
import { useSearchInput } from "hooks/useSearchInput";
import ReactECharts from "echarts-for-react";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  TechnicalCommissionRates,
  TechnicalCommissionRow,
  TechnicalCommissionScope,
  TechnicalCommissionStatus,
  technicalCommissionStatusOptions,
  technicalCommissionStatusTrans,
} from "types/technicalCommission";
import { formatVND, getTitle } from "utils";
import { can } from "utils/auth";
import { barValueLabel } from "utils/chart";
import dayjs, { Dayjs } from "utils/dayjs";
import { StatusTabs } from "views/Opportunity/components/StatusTabs";

const technicalPath = "/commission-technical";

const formatMoney = (num: number) => `${formatVND(Math.round(num || 0))}đ`;
const displayDate = (value?: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "--");

const TABS: { key: string; label: string; tone: "red" | "orange" | "gold" | "green" | "blue" }[] = [
  { key: "ALL", label: "Tất cả", tone: "blue" },
  { key: "EMPTY", label: "Trống", tone: "gold" },
  { key: "NOT_COUNTED", label: "Không tính HH", tone: "red" },
  { key: "UNPAID", label: "Chưa trả HH", tone: "orange" },
  { key: "PAID", label: "Đã trả HH", tone: "green" },
];

const EMPTY_COUNTS: Record<string, number> = { ALL: 0, EMPTY: 0, NOT_COUNTED: 0, UNPAID: 0, PAID: 0 };

type Props = { title?: string; scope: TechnicalCommissionScope };

export const TechnicalCommissionPage = observer(({ title = "", scope }: Props) => {
  const isTechnical = scope === "technical";
  const person = scope === "chuong" ? "Chương" : "Dung";
  const canUpdate = isTechnical && can(technicalPath, "UPDATE");

  const [tab, setTab] = useState("ALL");
  const searchInput = useSearchInput();
  const search = searchInput.debounced;
  const [paging, setPaging] = useState({ page: 1, limit: 50 });
  const page = paging.page;
  const resetPage = useCallback(() => setPaging((prev) => ({ ...prev, page: 1 })), []);
  const [rows, setRows] = useState<TechnicalCommissionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>(EMPTY_COUNTS);
  const [totalCommission, setTotalCommission] = useState(0);
  const [rates, setRates] = useState<TechnicalCommissionRates>({ chuong: 0, dung: 0 });
  const [loading, setLoading] = useState(false);

  const [monthValue, setMonthValue] = useState<Dayjs>(dayjs().startOf("month"));
  const [chartRows, setChartRows] = useState<{ month: number; amount: number }[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const year = monthValue.year();
  const isCurrentMonth = monthValue.isSame(dayjs(), "month");

  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<TechnicalCommissionStatus | undefined>();

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await technicalCommissionApi.findAll({
        scope,
        page,
        limit: paging.limit,
        search,
        status: isTechnical && tab !== "ALL" ? tab : undefined,
        month: isTechnical ? undefined : monthValue.month() + 1,
        year: isTechnical ? undefined : monthValue.year(),
      });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setCounts({ ...EMPTY_COUNTS, ...(res.data.counts || {}) });
      setTotalCommission(res.data.totalCommission || 0);
      setRates(res.data.rates || { chuong: 0, dung: 0 });
    } finally {
      setLoading(false);
    }
  }, [scope, page, paging.limit, search, tab, isTechnical, monthValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchChart = useCallback(async () => {
    if (isTechnical) return;
    setChartLoading(true);
    try {
      const res = await technicalCommissionApi.monthlyChart(scope as "chuong" | "dung", year);
      setChartRows(res.data.rows || []);
    } finally {
      setChartLoading(false);
    }
  }, [isTechnical, scope, year]);

  useEffect(() => {
    fetchChart();
  }, [fetchChart]);

  // Chỉ vẽ các tháng đã tới
  const visibleChartRows = useMemo(() => {
    const today = dayjs();
    return chartRows.filter((row) => year < today.year() || (year === today.year() && row.month <= today.month() + 1));
  }, [chartRows, year]);

  const yearTotal = useMemo(() => visibleChartRows.reduce((sum, row) => sum + row.amount, 0), [visibleChartRows]);

  const chartOption = useMemo(
    () => ({
      grid: { left: 16, right: 24, top: 22, bottom: 4, containLabel: true },
      tooltip: { trigger: "axis", valueFormatter: (value: number) => formatMoney(Number(value)) },
      xAxis: { type: "category", data: visibleChartRows.map((row) => `T${row.month}`) },
      yAxis: {
        type: "value",
        splitNumber: 3,
        axisLabel: {
          formatter: (value: number) =>
            Math.abs(value) >= 1_000_000 ? `${+(value / 1_000_000).toFixed(1)}tr`.replace(".", ",") : formatVND(value),
        },
      },
      series: [
        {
          type: "bar",
          data: visibleChartRows.map((row) => row.amount),
          itemStyle: { color: "#1fa6aa", borderRadius: [4, 4, 0, 0] },
          barMaxWidth: 36,
          label: barValueLabel("top", 10),
        },
      ],
    }),
    [visibleChartRows],
  );

  const applyStatus = useCallback(
    async (ids: number[], status: TechnicalCommissionStatus | null) => {
      await technicalCommissionApi.updateStatus(ids, status);
      message.success(`Đã cập nhật ${ids.length} dòng`);
      setSelectedKeys([]);
      setBulkStatus(undefined);
      fetchData();
    },
    [fetchData],
  );

  const requestStatusChange = useCallback(
    (ids: number[], status: TechnicalCommissionStatus | null) => {
      if (status !== "PAID") {
        applyStatus(ids, status);
        return;
      }
      Modal.confirm({
        title: "Xác nhận đã trả hoa hồng",
        content: `Chuyển ${ids.length} dòng sang "Đã trả HH". Số tiền HH Chương (${rates.chuong}%) và HH Dung (${rates.dung}%) sẽ được chốt theo tỷ lệ hiện tại, sau này đổi % cấu hình cũng không thay đổi.`,
        okText: "Xác nhận",
        cancelText: "Hủy",
        onOk: () => applyStatus(ids, status),
      });
    },
    [applyStatus, rates],
  );

  const changePaidDate = useCallback(
    async (id: number, date: Dayjs) => {
      await technicalCommissionApi.updatePaidDate(id, date.format("YYYY-MM-DD"));
      message.success("Đã cập nhật ngày trả hoa hồng");
      fetchData();
    },
    [fetchData],
  );

  const columns = useMemo<TableColumnsType<TechnicalCommissionRow>>(() => {
    const commissionCell = (row: TechnicalCommissionRow, value: number) => {
      if (row.technicalCommissionStatus === "NOT_COUNTED") return <span className="text-gray-300">--</span>;
      const fixed = row.technicalCommissionStatus === "PAID";
      return <span className={fixed ? "font-semibold text-green-700" : "text-gray-500"}>{formatMoney(value)}</span>;
    };

    const base: TableColumnsType<TechnicalCommissionRow> = [
      {
        title: "Đơn hàng",
        key: "order",
        render: (_, row) => (
          <div>
            <div className="font-semibold">{row.order?.code || "--"}</div>
            <div className="text-xs text-gray-500">{row.order?.opportunityName || "--"}</div>
          </div>
        ),
      },
      { title: "Mã hợp đồng", key: "contractCode", render: (_, row) => <TableContent>{row.order?.contractCode || "--"}</TableContent> },
      { title: "Khách hàng", key: "customer", render: (_, row) => <TableContent>{row.customer?.name || "--"}</TableContent> },
      {
        title: "Đợt thu",
        key: "content",
        render: (_, row) => (
          <TableContent>{row.content ? `${row.content}${row.debtCount ? ` / ${row.debtCount} đợt` : ""}` : "--"}</TableContent>
        ),
      },
      ...(isTechnical
        ? [{ title: "Ngày thu", dataIndex: "collectedDate", key: "collectedDate", render: (value: string) => displayDate(value) }]
        : []),
      ...(isTechnical
        ? [
            {
              title: "Số tiền công nợ",
              dataIndex: "amount",
              key: "amount",
              align: "right" as const,
              render: (value: number) => formatMoney(value),
            },
          ]
        : []),
      {
        title: (
          <span>
            Tiền tính HH{" "}
            <Tooltip title="Cơ sở tính hoa hồng kỹ thuật: số tiền công nợ đã trừ VAT (giống hoa hồng SALE).">
              <QuestionCircleOutlined className="text-gray-400 cursor-help" />
            </Tooltip>
          </span>
        ),
        dataIndex: "preTaxAmount",
        key: "preTaxAmount",
        align: "right",
        render: (value) => formatMoney(value),
      },
    ];

    if (isTechnical) {
      base.push({
        title: "Trạng thái HH",
        key: "technicalCommissionStatus",
        width: 190,
        render: (_, row) =>
          canUpdate ? (
            <Select
              allowClear
              size="small"
              placeholder="Trống"
              style={{ width: 160 }}
              value={row.technicalCommissionStatus || undefined}
              options={technicalCommissionStatusOptions}
              onChange={(value) => requestStatusChange([row.id], (value as TechnicalCommissionStatus) || null)}
            />
          ) : row.technicalCommissionStatus ? (
            <StatusCell value={row.technicalCommissionStatus} trans={technicalCommissionStatusTrans} />
          ) : (
            <span className="text-gray-400">Trống</span>
          ),
      });
      base.push({
        title: "Ngày trả hoa hồng",
        dataIndex: "technicalCommissionPaidAt",
        key: "technicalCommissionPaidAt",
        width: 170,
        render: (value, row) => {
          if (row.technicalCommissionStatus !== "PAID") return "--";
          if (!canUpdate) return value ? dayjs.unix(value).format("DD/MM/YYYY") : "--";
          return (
            <DatePicker
              size="small"
              allowClear={false}
              format="DD/MM/YYYY"
              style={{ width: 130 }}
              value={value ? dayjs.unix(value) : undefined}
              onChange={(date) => date && changePaidDate(row.id, date)}
            />
          );
        },
      });
      base.push({
        title: `HH Chương (${rates.chuong}%)`,
        dataIndex: "commissionChuong",
        key: "commissionChuong",
        align: "right",
        render: (value, row) => commissionCell(row, value),
      });
      base.push({
        title: `HH Dung (${rates.dung}%)`,
        dataIndex: "commissionDung",
        key: "commissionDung",
        align: "right",
        render: (value, row) => commissionCell(row, value),
      });
    } else {
      base.push({
        title: "Ngày trả hoa hồng",
        dataIndex: "technicalCommissionPaidAt",
        key: "technicalCommissionPaidAt",
        render: (value) => (value ? dayjs.unix(value).format("DD/MM/YYYY") : "--"),
      });
      base.push({
        title: `HH ${person}`,
        key: "commission",
        align: "right",
        render: (_, row) => (
          <span className="font-semibold text-green-700">
            {formatMoney(scope === "chuong" ? row.commissionChuong : row.commissionDung)}
          </span>
        ),
      });
    }

    return base;
  }, [isTechnical, canUpdate, rates, requestStatusChange, changePaidDate, person, scope]);

  const { showingFrom, showingTo } = getTableShowingRange(paging.page, paging.limit, total);

  const changeMonth = (next: Dayjs) => {
    resetPage();
    setMonthValue(next);
  };

  return (
    <div className="flex flex-col gap-4">
      {!isTechnical && (
        <>
          <Card bodyStyle={{ padding: 16 }}>
            <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3">
              <div className="flex items-center gap-1">
                <AppButton tone="light" aria-label="Tháng trước" onClick={() => changeMonth(monthValue.subtract(1, "month"))}>
                  <LeftOutlined />
                </AppButton>
                <div className="min-w-[110px] text-center font-semibold">{monthValue.format("MM/YYYY")}</div>
                <AppButton
                  tone="light"
                  aria-label="Tháng sau"
                  disabled={isCurrentMonth}
                  onClick={() => changeMonth(monthValue.add(1, "month"))}
                >
                  <RightOutlined />
                </AppButton>
              </div>

              <div className="flex flex-wrap items-center gap-x-10 gap-y-2">
                <div>
                  <div className="text-xs text-gray-400 font-semibold uppercase">
                    Thu nhập HH {person} tháng {monthValue.format("MM/YYYY")}
                  </div>
                  <div className="text-xl font-bold text-green-700">{formatMoney(totalCommission)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-semibold uppercase">Thu nhập HH {person} năm {year}</div>
                  <div className="text-xl font-bold">{formatMoney(yearTotal)}</div>
                </div>
              </div>
            </div>
          </Card>

          <Card bodyStyle={{ padding: "12px 16px" }}>
            <div className="text-sm font-semibold">Thu nhập HH {person} theo tháng — năm {year}</div>
            <ReactECharts option={chartOption} style={{ height: 190 }} showLoading={chartLoading} />
          </Card>
        </>
      )}

      {isTechnical && (
        <StatusTabs
          activeKey={tab}
          onChange={(key) => {
            resetPage();
            setSelectedKeys([]);
            setTab(key);
          }}
          items={TABS.map((item) => ({ key: item.key, label: item.label, count: counts[item.key] || 0, tone: item.tone }))}
        />
      )}
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: searchInput.value,
              width: 420,
              placeholder: "Tìm theo mã đơn, hợp đồng, báo giá, khách hàng",
              onChange: (value) => {
                resetPage();
                searchInput.onChange(value);
              },
              inputProps: { allowClear: { clearIcon: <CloseCircleFilled onClick={searchInput.clear} /> } },
            }}
            leftActions={
              canUpdate && selectedKeys.length > 0 ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm">Đã chọn {selectedKeys.length} dòng</span>
                  <Select
                    allowClear
                    placeholder="Chọn trạng thái HH"
                    style={{ width: 180 }}
                    value={bulkStatus}
                    options={technicalCommissionStatusOptions}
                    onChange={setBulkStatus}
                  />
                  <AppButton
                    tone="primary"
                    disabled={!bulkStatus}
                    onClick={() => bulkStatus && requestStatusChange(selectedKeys, bulkStatus)}
                  >
                    Áp dụng
                  </AppButton>
                  <AppButton tone="light" onClick={() => requestStatusChange(selectedKeys, null)}>
                    Đặt về Trống
                  </AppButton>
                </div>
              ) : null
            }
            filterLabel={null}
          />
        }
        table={
          <AppTable<TechnicalCommissionRow>
            rowKey="id"
            loading={loading}
            dataSource={rows}
            columns={columns}
            rowSelection={
              canUpdate
                ? { selectedRowKeys: selectedKeys, onChange: (keys) => setSelectedKeys(keys as number[]) }
                : undefined
            }
            pagination={false}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={setPaging}
          />
        }
      />
    </div>
  );
});
